import React from "react";
import { errorMessage, ApiError } from "@atlas/shared";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { Check, Zap, Lock, Gift, Award, AlertTriangle, History } from "lucide-react";
import {
  fetchBillingStatus,
  startCheckout,
  startFullHistoryCheckout,
  openBillingPortal,
  redeemKey,
} from "../api/billing";
import { createStoreCheckout } from "../api/store";
import { BetaGateModal } from "../components/BetaGateModal";
import { MonthlyHistoryNotice, confirmMonthlyWithoutHistory } from "../components/MonthlyHistoryNotice";
import { useBetaGate } from "../hooks/useFeatureFlags";
import { Button } from "../components/Button";
import { Badge } from "../components/Badge";
import { AtlasPanel } from "../components/AtlasPanel";
import { useAuth } from "../hooks/useAuth";
import { EmailCaptureCard } from "../components/EmailCaptureCard";
import {
  PRO_MONTHLY_GBP,
  PRO_ANNUAL_PER_MONTH_GBP,
  PRO_ANNUAL_TOTAL_GBP,
  PRO_ANNUAL_SAVING_PERCENT,
  PRO_ANNUAL_NOTE,
  PRO_MONTHLY_NOTE,
  PRO_FEATURES,
  FULL_HISTORY_PAY_LINE,
  FULL_HISTORY_GBP,
} from "../lib/pricing";

// Supporters bundle is hidden until further notice — fulfilment isn't
// implemented and the price is a placeholder. Flip to true to bring it back
// (keep in sync with the supporters-bundle `active` flag in db seeds/store.ts).
const SUPPORTERS_BUNDLE_ENABLED = false;

// ─── Plan feature lists ───────────────────────────────────────────────────────

const FREE_FEATURES = [
  "Unlimited peak and trail tracking",
  "Strava and health app sync",
  "Matches your last 12 months against every collection",
  "3 GPX uploads a month",
  "Region coverage map",
];


function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ─── Current-plan copy by source (spec 24) ────────────────────────────────────
// Branches on where Pro comes from and the billing state, so a store
// subscriber never sees a Stripe portal button and a past-due card keeps Pro
// with an honest notice.

type Billing = import("@atlas/shared").BillingStatus;

function planSummary(billing: Billing | undefined, isPro: boolean): string {
  if (!isPro) return "3 GPX uploads a month · last 12 months matched";
  if (!billing) return "Atlas Pro";
  const expires = billing.planExpiresAt ? formatDate(billing.planExpiresAt) : null;
  switch (billing.provider) {
    case "stripe": {
      const price = billing.interval === "year" ? PRO_ANNUAL_PER_MONTH_GBP : PRO_MONTHLY_GBP;
      const cadence = billing.interval === "year" ? "annually" : "monthly";
      if (billing.dunning.pastDue) return `£${price}/mo billed ${cadence} · payment pending`;
      if (billing.renewsAt) {
        return `£${price}/mo billed ${cadence} · ${billing.cancelAtPeriodEnd ? "ends" : "renews"} ${formatDate(billing.renewsAt)}`;
      }
      return "Subscription · active";
    }
    case "apple":
      return `Managed through the App Store${expires ? ` · renews ${expires}` : ""}`;
    case "google":
      return `Managed through Google Play${expires ? ` · renews ${expires}` : ""}`;
    case "key":
      return expires ? `Pro until ${expires} · via a plan key` : "Pro · via a plan key";
    case "invite":
      return expires ? `Pro until ${expires} · as part of the beta` : "Pro · as part of the beta";
    case "admin":
      return expires ? `Pro until ${expires} · complimentary` : "Pro · complimentary";
    case "supporters":
      return "Lifetime Pro · founding supporter";
    default:
      return expires ? `Pro until ${expires}` : "Atlas Pro";
  }
}

// ─── Full history (spec 30) ───────────────────────────────────────────────────
// A permanent unlock, separate from Pro: says where it came from when it is on.

const FULL_HISTORY_SOURCE_LABEL: Record<import("@atlas/shared").FullHistorySource, string> = {
  stripe: "bought on the web",
  apple: "bought in the App Store",
  google: "bought on Google Play",
  annual: "included with annual Pro",
  supporter: "included with the supporters bundle",
  admin: "complimentary",
  partner: "via a partner link",
};

function fullHistorySummary(fh: import("@atlas/shared").FullHistoryState): string {
  const parts = [fh.source ? FULL_HISTORY_SOURCE_LABEL[fh.source] : null, fh.since ? `since ${formatDate(fh.since)}` : null];
  return parts.filter(Boolean).join(" · ");
}

function Notice({ tone, children }: { tone: "danger" | "neutral"; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        marginTop: 14,
        padding: "10px 14px",
        background: tone === "danger" ? "var(--danger-soft)" : "var(--surface-sunken)",
        border: `1px solid ${tone === "danger" ? "rgba(224, 80, 63, 0.4)" : "var(--border-subtle)"}`,
        borderRadius: "var(--radius-md)",
        fontSize: 13,
        color: "var(--text-secondary)",
        lineHeight: 1.5,
      }}
    >
      {tone === "danger" && <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2, color: "var(--status-danger)" }} />}
      <div>{children}</div>
    </div>
  );
}

// ─── Billing period toggle ─────────────────────────────────────────────────────
// Mirrors the monthly/annual toggle on the landing page's pricing section.

function BillingIntervalToggle({
  value,
  onChange,
}: {
  value: "month" | "year";
  onChange: (interval: "month" | "year") => void;
}) {
  const optionStyle = (active: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: "var(--radius-pill)",
    border: "none",
    background: active ? "var(--surface-card)" : "transparent",
    color: active ? "var(--text-primary)" : "var(--text-muted)",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "var(--font-sans)",
    cursor: "pointer",
    boxShadow: active ? "var(--shadow-sm)" : "none",
  });

  return (
    <div
      role="group"
      aria-label="Billing period"
      style={{
        display: "inline-flex",
        padding: 4,
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-pill)",
        gap: 4,
      }}
    >
      <button type="button" style={optionStyle(value === "month")} onClick={() => onChange("month")}>
        Monthly
      </button>
      <button type="button" style={optionStyle(value === "year")} onClick={() => onChange("year")}>
        Annual <Badge variant="success">Save 25%</Badge>
      </button>
    </div>
  );
}

// A `verify_email` 403 from POST /billing/checkout (placeholder or unverified
// address) is a prompt, not a failure: show the email card instead of red text.
function isVerifyEmailError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.body?.["error"] === "verify_email";
}

// ─── PlanPage ─────────────────────────────────────────────────────────────────

export default function PlanPage() {
  const betaGate = useBetaGate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [keyInput, setKeyInput] = React.useState("");
  const [keySuccess, setKeySuccess] = React.useState<string | null>(null);
  const [billingInterval, setBillingInterval] = React.useState<"month" | "year">("year");
  const [betaGateOpen, setBetaGateOpen] = React.useState(false);

  const search = useSearch({ from: "/_protected/plan" });
  const checkoutResult = (search as Record<string, string>)["checkout"];
  const checkoutProduct = (search as Record<string, string>)["product"];

  const billingQuery = useQuery({
    queryKey: ["billing"],
    queryFn: fetchBillingStatus,
    staleTime: 60_000,
  });

  const checkoutMutation = useMutation({
    mutationFn: (interval: "month" | "year") => startCheckout(interval),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });

  const fullHistoryMutation = useMutation({
    mutationFn: startFullHistoryCheckout,
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
    onError: (err) => {
      // Already unlocked elsewhere (another tab, the app): refresh the card.
      if (err instanceof ApiError && err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ["billing"] });
      }
    },
  });

  const portalMutation = useMutation({
    mutationFn: openBillingPortal,
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });

  const redeemMutation = useMutation({
    mutationFn: (code: string) => redeemKey(code),
    onSuccess: (data) => {
      setKeySuccess(`Key redeemed! Your plan is now ${data.plan}${data.expiresAt ? ` until ${new Date(data.expiresAt).toLocaleDateString()}` : ""}.`);
      setKeyInput("");
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  const supportersCheckoutMutation = useMutation({
    mutationFn: () => createStoreCheckout({ items: [{ slug: "supporters-bundle", quantity: 1 }] }),
    onSuccess: ({ url }) => {
      if (url) window.location.assign(url);
    },
  });

  const billing = billingQuery.data;
  const lacksFullHistory = billing?.fullHistory?.unlocked !== true;
  const startProCheckout = () => {
    if (betaGate) {
      setBetaGateOpen(true);
      return;
    }
    if (billingInterval === "month" && lacksFullHistory && !confirmMonthlyWithoutHistory()) return;
    checkoutMutation.mutate(billingInterval);
  };
  const isPro = billing?.isPro ?? user?.plan === "pro";
  const usedThisMonth = billing?.limits.gpxImportsUsedThisMonth ?? 0;
  const monthlyLimit = billing?.limits.gpxImportsPerMonth;
  const isFoundingSupporter = billing?.billingStatus === "lifetime" || billing?.provider === "supporters";
  const provider = billing?.provider ?? null;
  const pastDue = billing?.dunning.pastDue === true;
  const timedSource = provider === "key" || provider === "invite" || provider === "admin";
  const fullHistory = billing?.fullHistory;

  return (
    <div
      style={{
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div
        className="atlas-topo"
        style={{ position: "relative", padding: "28px 28px 24px" }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(0deg, var(--bg-app) 0%, color-mix(in srgb, var(--bg-app) 40%, transparent) 100%)",
          }}
        />
        <div style={{ position: "relative" }}>
          <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Subscription</p>
          <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
            Plan & Billing
          </h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 6, marginBottom: 0 }}>
            Manage your Atlas subscription and usage.
          </p>
        </div>
      </div>

      <div className="r-page-x" style={{ padding: "0 28px 28px", display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── Checkout result banner ───────────────────────────────────── */}
        {checkoutResult === "success" && (
          <div style={{
            padding: "12px 16px",
            background: "var(--status-success-soft, rgba(34,197,94,.1))",
            border: "1px solid var(--status-success)",
            borderRadius: "var(--radius-md)",
            fontSize: 14,
            color: "var(--status-success)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}>
            <Check size={16} />
            {checkoutProduct === "full_history"
              ? "Full history is unlocked. Your older activities are being matched now."
              : "Pro is on. Your subscription is now active."}
          </div>
        )}
        {checkoutResult === "cancelled" && (
          <div style={{
            padding: "12px 16px",
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            fontSize: 14,
            color: "var(--text-secondary)",
          }}>
            Checkout cancelled. You're still on the Free plan.
          </div>
        )}

        {/* ── Current plan ─────────────────────────────────────────────── */}
        <AtlasPanel title="Current plan">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: "var(--radius-md)",
                background: isPro ? "var(--accent-soft)" : "var(--surface-sunken)",
                border: `1px solid ${isPro ? "var(--border-gold)" : "var(--border-subtle)"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: isPro ? "var(--gold-400)" : "var(--text-muted)",
              }}>
                {isPro ? <Zap size={20} /> : <Lock size={20} />}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>
                  {isPro ? "Atlas Pro" : "Atlas Free"}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                  {planSummary(billing, isPro)}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              {isPro && provider === "stripe" ? (
                <Button
                  variant={pastDue ? "primary" : "ghost"}
                  size="sm"
                  onClick={() => portalMutation.mutate()}
                  disabled={portalMutation.isPending}
                >
                  {portalMutation.isPending ? "Opening…" : pastDue ? "Update payment method" : "Manage subscription"}
                </Button>
              ) : isPro && timedSource ? (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Zap size={14} />}
                  onClick={startProCheckout}
                  disabled={checkoutMutation.isPending}
                >
                  Keep Pro after {billing?.planExpiresAt ? formatDate(billing.planExpiresAt) : "it ends"}
                </Button>
              ) : !isPro ? (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Zap size={14} />}
                  onClick={startProCheckout}
                  disabled={checkoutMutation.isPending}
                >
                  {checkoutMutation.isPending ? "Loading…" : "Upgrade to Pro"}
                </Button>
              ) : null}
            </div>
          </div>

          {/* State notices (spec 24) */}
          {isPro && pastDue && (
            <Notice tone="danger">
              Your last payment did not go through. Update your card to keep Pro; we retry for a few days
              {billing?.dunning.retryUntil ? ` (next attempt around ${formatDate(billing.dunning.retryUntil)})` : ""}.
              Pro stays on in the meantime.
            </Notice>
          )}
          {isPro && billing?.graceUntil && (
            <Notice tone="danger">
              There is a payment issue with your {provider === "apple" ? "App Store" : "Google Play"} account.
              Pro stays on until {formatDate(billing.graceUntil)} while the store retries; fix the payment
              method in your store settings to keep it.
            </Notice>
          )}
          {isPro && (provider === "apple" || provider === "google") && !billing?.graceUntil && (
            <Notice tone="neutral">
              {provider === "apple"
                ? "Cancel or change your plan from Settings › your name › Subscriptions on your iPhone."
                : "Cancel or change your plan from Google Play › Payments and subscriptions on your Android device."}
            </Notice>
          )}
          {!isPro && billing?.plan === "pro" && (
            <Notice tone="neutral">
              Your Pro plan has ended. Everything you collected is still yours, and any Full history you
              unlocked stays on. Pro adds unlimited route checks, Insights, Wrapped, unlimited custom lists,
              250 Waypoints a month and offline maps on mobile.
            </Notice>
          )}

          {/* Usage bar for free users */}
          {!isPro && monthlyLimit != null && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>
                <span>GPX uploads this month</span>
                <span style={{ fontFamily: "var(--font-mono)" }}>{usedThisMonth} / {monthlyLimit}</span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: "var(--surface-sunken)", overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min(100, (usedThisMonth / monthlyLimit) * 100)}%`,
                  background: usedThisMonth >= monthlyLimit ? "var(--status-danger)" : "var(--accent)",
                  borderRadius: 3,
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}
        </AtlasPanel>

        {/* ── Billing period toggle ────────────────────────────────────── */}
        {!isPro && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <BillingIntervalToggle value={billingInterval} onChange={setBillingInterval} />
          </div>
        )}
        {!isPro && billingInterval === "month" && lacksFullHistory && (
          <MonthlyHistoryNotice
            onChooseYearly={() => setBillingInterval("year")}
            onUnlockFullHistory={
              fullHistory
                ? () => (betaGate ? setBetaGateOpen(true) : fullHistoryMutation.mutate())
                : undefined
            }
          />
        )}

        {/* ── Plan comparison ──────────────────────────────────────────── */}
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* Free */}
          <AtlasPanel title="">
            <div style={{ padding: "4px 0" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Free</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, color: "var(--text-primary)", marginBottom: 16 }}>
                £0<span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-muted)" }}>/mo</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {FREE_FEATURES.map((f) => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                    <Check size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </AtlasPanel>

          {/* Pro */}
          <AtlasPanel title="">
            <div style={{ padding: "4px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-accent)" }}>Pro</div>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                  background: "var(--accent)", color: "var(--text-on-gold)",
                  padding: "2px 6px", borderRadius: "var(--radius-pill)",
                }}>RECOMMENDED</span>
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, color: "var(--text-accent)", marginBottom: 4 }}>
                £{billingInterval === "year" ? PRO_ANNUAL_PER_MONTH_GBP : PRO_MONTHLY_GBP}
                <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text-muted)" }}>/mo</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
                {billingInterval === "year"
                  ? `Billed annually at £${PRO_ANNUAL_TOTAL_GBP}, ${PRO_ANNUAL_SAVING_PERCENT}% off the monthly price. ${PRO_ANNUAL_NOTE}`
                  : `Billed monthly. ${PRO_MONTHLY_NOTE} Annual includes it and saves ${PRO_ANNUAL_SAVING_PERCENT}%.`}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {PRO_FEATURES.map((f) => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-secondary)" }}>
                    <Check size={14} style={{ color: "var(--text-accent)", flexShrink: 0 }} />
                    {f}
                  </div>
                ))}
                {billing?.allowance?.nextGrantOn && (
                  <div style={{ fontSize: 12, color: "var(--text-muted)", paddingLeft: 22 }}>
                    Next 250 Waypoints on {formatDate(billing.allowance.nextGrantOn)}
                  </div>
                )}
              </div>
              {!isPro && (
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Zap size={14} />}
                  onClick={startProCheckout}
                  disabled={checkoutMutation.isPending}
                  style={{ width: "100%", marginTop: 20 }}
                >
                  {checkoutMutation.isPending ? "Loading…" : "Upgrade to Pro"}
                </Button>
              )}
              {checkoutMutation.isError && !isVerifyEmailError(checkoutMutation.error) && (
                <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 8 }}>
                  {errorMessage(checkoutMutation.error, "Failed to start checkout")}
                </p>
              )}
              {checkoutMutation.isError && isVerifyEmailError(checkoutMutation.error) && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
                    One thing first: Pro receipts and billing notices go by email, so we need an
                    address we can reach. Sort it below and try again.
                  </p>
                  <EmailCaptureCard compact />
                </div>
              )}
            </div>
          </AtlasPanel>
        </div>

        {/* ── Full history (spec 30) ───────────────────────────────────── */}
        {fullHistory && (
          <AtlasPanel>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: "var(--radius-md)",
                    background: fullHistory.unlocked ? "var(--accent-soft)" : "var(--surface-sunken)",
                    border: `1px solid ${fullHistory.unlocked ? "var(--border-gold)" : "var(--border-subtle)"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: fullHistory.unlocked ? "var(--gold-400)" : "var(--text-muted)",
                  }}
                >
                  <History size={20} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>Full history</p>
                  {fullHistory.unlocked ? (
                    <>
                      <div style={{ fontSize: 14, color: "var(--text-primary)", fontWeight: 600, marginBottom: 2 }}>
                        {fullHistory.source === "annual" ? "Included with your annual plan" : "Unlocked"}
                      </div>
                      <div style={{ fontSize: 13, color: "var(--text-secondary)", maxWidth: 520, lineHeight: 1.5 }}>
                        Atlas matches your full history from every source, with no monthly GPX limit.
                        {fullHistorySummary(fullHistory) ? ` ${fullHistorySummary(fullHistory).replace(/^./, (ch) => ch.toUpperCase())}.` : ""}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, color: "var(--text-primary)", fontWeight: 600, marginBottom: 2 }}>
                        Match your full history, once · £{FULL_HISTORY_GBP}
                      </div>
                      <div style={{ fontSize: 13, color: "var(--text-secondary)", maxWidth: 520, lineHeight: 1.5 }}>
                        Atlas matches your full history, not just the last 12 months, for every source including
                        ones you connect later. It removes the monthly GPX upload limit. Pay once. Annual Pro includes it.
                      </div>
                    </>
                  )}
                </div>
              </div>
              {!fullHistory.unlocked && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, maxWidth: 260 }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<History size={14} />}
                    disabled={fullHistoryMutation.isPending}
                    onClick={() => (betaGate ? setBetaGateOpen(true) : fullHistoryMutation.mutate())}
                  >
                    {fullHistoryMutation.isPending ? "Opening…" : "Unlock full history"}
                  </Button>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right", lineHeight: 1.4 }}>
                    {FULL_HISTORY_PAY_LINE}
                  </span>
                </div>
              )}
            </div>
            {fullHistoryMutation.isError && isVerifyEmailError(fullHistoryMutation.error) && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
                  One thing first: receipts go by email, so we need an address we can reach. Sort it
                  below and try again.
                </p>
                <EmailCaptureCard compact />
              </div>
            )}
            {fullHistoryMutation.isError &&
              !isVerifyEmailError(fullHistoryMutation.error) &&
              !(fullHistoryMutation.error instanceof ApiError && fullHistoryMutation.error.status === 409) && (
                <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 10 }}>
                  {errorMessage(fullHistoryMutation.error, "Failed to start checkout")}
                </p>
              )}
          </AtlasPanel>
        )}

        {/* ── Supporters bundle (STO-6) ───────────────────────────────────── */}
        {SUPPORTERS_BUNDLE_ENABLED && (
        <AtlasPanel>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: "var(--radius-md)",
                  background: "var(--surface-sunken)",
                  border: "1px solid var(--border-subtle)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-muted)",
                }}
              >
                {isFoundingSupporter ? <Award size={20} /> : <Gift size={20} />}
              </div>
              <div>
                <p className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>Supporters</p>
                {isFoundingSupporter ? (
                  <div style={{ fontSize: 14, color: "var(--text-primary)", fontWeight: 600 }}>
                    You're a founding supporter
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 14, color: "var(--text-primary)", fontWeight: 600, marginBottom: 2 }}>
                      Prefer to pay once?
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-secondary)", maxWidth: 480, lineHeight: 1.5 }}>
                      The Supporters bundle is lifetime Pro with Full history, exclusive Founding Supporter cosmetics and future
                      merch, £129.99.
                    </div>
                  </>
                )}
              </div>
            </div>
            {!isFoundingSupporter && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Gift size={14} />}
                disabled={supportersCheckoutMutation.isPending}
                onClick={() =>
                  betaGate ? setBetaGateOpen(true) : supportersCheckoutMutation.mutate()
                }
              >
                {supportersCheckoutMutation.isPending ? "Opening…" : "Get the Supporters bundle"}
              </Button>
            )}
          </div>
          {supportersCheckoutMutation.isError && (
            <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 10 }}>
              {errorMessage(supportersCheckoutMutation.error, "Failed to start checkout")}
            </p>
          )}
        </AtlasPanel>
        )}

        {/* ── Redeem key ───────────────────────────────────────────────── */}
        <AtlasPanel title="Redeem a key">
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 14 }}>
            Have an access key? Enter it below to activate your plan.
          </p>
          {keySuccess ? (
            <div style={{
              padding: "10px 14px",
              background: "var(--accent-soft)",
              border: "1px solid var(--border-gold)",
              borderRadius: "var(--radius-md)",
              fontSize: 13,
              color: "var(--text-accent)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}>
              <Check size={14} /> {keySuccess}
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (keyInput.trim()) redeemMutation.mutate(keyInput.trim());
              }}
              style={{ display: "flex", gap: 8 }}
            >
              <input
                type="text"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value.toUpperCase())}
                style={{
                  flex: 1,
                  height: 36,
                  padding: "0 12px",
                  background: "var(--surface-sunken)",
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius-md)",
                  color: "var(--text-primary)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  letterSpacing: "0.05em",
                  outline: "none",
                }}
              />
              <Button
                type="submit"
                variant="secondary"
                size="sm"
                disabled={!keyInput.trim() || redeemMutation.isPending}
              >
                {redeemMutation.isPending ? "Redeeming…" : "Redeem"}
              </Button>
            </form>
          )}
          {redeemMutation.isError && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 8 }}>
              {errorMessage(redeemMutation.error, "Redemption failed")}
            </p>
          )}
        </AtlasPanel>
      </div>

      {betaGateOpen && <BetaGateModal onClose={() => setBetaGateOpen(false)} />}
    </div>
  );
}
